import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as amplitude from "@amplitude/analytics-browser";
import { Button, Badge } from '@unified-checkout/design-system';
import {
  Pen,
  Eye,
  Gear,
  PaperPlane,
  CircleInfo,
  ArrowRight,
} from '@unified-checkout/design-system/icons/index';
import { INNER_ROUTES } from '@app/dashboard/constants/inner-routes';
import { userInfo } from '@app/dashboard/utils/auth/userInfo';
import { getFromLocalStorage } from '@app/dashboard/utils/manageLocalStorage';
import {
  createPaymentHandle,
  updatePaymentHandleName,
} from '@app/dashboard/services/inner_layout/payme';
import { getPayMeErrorMessage } from '@app/dashboard/views/pages/inner_layout/PayMe/utils/errorMessage';
import {
  PAYME_HANDLE_MAX_LENGTH,
  isValidPayMeHandle,
  sanitizeSuggestedHandle,
} from '@app/dashboard/views/pages/inner_layout/PayMe/utils/handleValidation';
import { QUERY_KEYS } from '@app/dashboard/constants/query-keys';
import { PAYME_BASE_URL } from '@app/dashboard/constants/payme-links';
import CopyText from '@app/dashboard/components/inner_layout/CopyText';
import { PayMeHandleInput } from './PayMeHandleInput';
import { PayMeShareModal } from './PayMeShareModal';
import { PayMeCheckoutPreviewModal } from './PayMeCheckoutPreviewModal';
import { EditHandleConfirmModal } from './EditHandleConfirmModal';
import { EditHandleModal } from './EditHandleModal';
import { FirstTimeSetupModal } from './FirstTimeSetupModal';
import { usePaymentHandleConfig } from '@app/dashboard/utils/paymentHandleConfig';
import { getCustomizationListing } from '@app/dashboard/services/inner_layout/checkout_customization/listing';
import { useTestLiveModeStore } from '@app/dashboard/store/testLiveMode';
import { notify } from '@app/dashboard/utils/notifier';
import { PERMISSIONS } from '@app/dashboard/constants/permissions';
import { PROFILE } from '@app/dashboard/constants/profile';
import { isAccessible } from '@app/dashboard/utils/isAllowedPermission';
import { getProfileType } from '@app/dashboard/utils/localStorageUtils';
import paymeIllustration from '@app/dashboard/assets/images/innerLayout/payme-card.png';
import { PAYME_EVENTS } from '@app/dashboard/constants/tracking_events/payme';
import styles from './payme-card.module.scss';

const RESPONSIVE_PREVIEW_BUTTON_WIDTH = 350; // Responsive-width preview button in pixels

export type PayMeCardVariant = 'promo' | 'handle';

export interface PayMeCardProps {
  variant?: PayMeCardVariant;
  handle?: string;
  isActivated?: boolean;
  canChangeHandleName?: boolean;
  logoUrl?: string | null;
}

export const PayMeCard = ({
  variant = 'promo',
  handle: handleProp,
  isActivated = true,
  canChangeHandleName = false,
  logoUrl,
}: PayMeCardProps) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { isLive } = useTestLiveModeStore();

  const isStaff = getProfileType() === PROFILE.TYPE.MERCHANT_STAFF;
  const isMerchant = getProfileType() === PROFILE.TYPE.MERCHANT;
  const canAddPaymentHandle = isStaff ? isAccessible(PERMISSIONS.CAN_ADD_PAYMENT_HANDLE) : isMerchant;
  const canChangePaymentHandle = isStaff ? isAccessible(PERMISSIONS.CAN_CHANGE_PAYMENT_HANDLE) : isMerchant;
  const canSharePaymentHandle = isStaff ? isAccessible(PERMISSIONS.CAN_SHARE_PAYMENT_HANDLE) : isMerchant;
  const hasAddOrChangePermission = canAddPaymentHandle || canChangePaymentHandle;

  // Handle creation state (only for 'handle' variant)
  const [handle, setHandle] = useState(handleProp || '');
  const [isHandleCreated, setIsHandleCreated] = useState(true);
  const [hasInitialized, setHasInitialized] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [isEditConfirmModalOpen, setIsEditConfirmModalOpen] = useState(false);
  const [isEditHandleModalOpen, setIsEditHandleModalOpen] = useState(false);
  const [isFirstTimeSetupModalOpen, setIsFirstTimeSetupModalOpen] = useState(false);
  const [editHandleApiError, setEditHandleApiError] = useState<string | null>(null);
  const [inputContainerWidth, setInputContainerWidth] = useState(0);

  const inputContainerRef = useRef<HTMLDivElement>(null);

  const { data: paymentHandleConfig } = usePaymentHandleConfig({
    enabled: variant === 'handle' && isHandleCreated && !!handle,
  });

  const { data: checkoutCustomizationData } = useQuery({
    queryKey: [QUERY_KEYS.CUSTOMIZATION_LISTING, isLive],
    queryFn: () => getCustomizationListing(isLive).then((res) => res.data),
    enabled: variant === 'handle' && isHandleCreated && !!handle && isPreviewModalOpen && !isStaff,
  });
  
  // Clear errors when language changes
  useEffect(() => {
    setError(null);
    setEditHandleApiError(null);
  }, [i18n.language]);

  useEffect(() => {
    if (handleProp) {
      setHandle(handleProp);
      setIsHandleCreated(true);
    } else if (variant === 'handle') {
      // If no handle prop is provided in handle variant, handle is not created
      setIsHandleCreated(false);
    }
  }, [handleProp, variant]);

  // Initialize handle with business name or company name on first load (handle variant only)
  // Only if handle prop is not provided
  useEffect(() => {
    if (variant === 'handle' && !hasInitialized && !handleProp) {
      const user = userInfo();
      // Try business_name first, then fall back to company_name
      const businessName = getFromLocalStorage('business_name') || '';
      const suggestedHandle = businessName || user.companyName || '';
      if (suggestedHandle) {
        setHandle(sanitizeSuggestedHandle(suggestedHandle));
      }
      setHasInitialized(true);
    }
  }, [variant, hasInitialized, handleProp]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setHandle(newValue);
    
    // Validate max length
    if (newValue.length > PAYME_HANDLE_MAX_LENGTH) {
      setError(
        t('innerLayout.paymePage.handleCreationCard.maxLengthError')
      );
    } else if (newValue.trim() && !isValidPayMeHandle(newValue)) { // Validate pattern
      setError(
        t('innerLayout.paymePage.handleCreationCard.invalidPattern')
      );
    } else {
      // Clear error when user starts typing and length is valid
      if (error) {
        setError(null);
      }
    }
  };


  // Measure input container width for responsive-width preview button
  useEffect(() => {
    if (variant !== 'handle' || !inputContainerRef.current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setInputContainerWidth(entry.contentRect.width);
      }
    });

    resizeObserver.observe(inputContainerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [variant]);

  const handleCreateLink = async () => {
    if (variant === 'promo') {
      amplitude.track(PAYME_EVENTS.PAYME_HOME_SELECTED);
      navigate(INNER_ROUTES.PAYME);
      return;
    }

    // Handle variant - create payment handle
    if (!handle.trim()) {
      return;
    }

    if (handle.length > PAYME_HANDLE_MAX_LENGTH) {
      setError(
        t('innerLayout.paymePage.handleCreationCard.maxLengthError')
      );
      return;
    }

    if (!isValidPayMeHandle(handle)) {
      setError(
        t('innerLayout.paymePage.handleCreationCard.invalidPattern')
      );
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      await createPaymentHandle({
        handle_name: handle.trim(),
      });

      // Success - handle created
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_PAYMENT_HANDLE_CONFIG],
      });
      setIsHandleCreated(true);
      setError(null);
      setIsFirstTimeSetupModalOpen(true);
      notify.success(t('innerLayout.paymePage.handleCreationCard.createSuccess'), {
        maxWidth: "32rem",
      });

      amplitude.track(PAYME_EVENTS.PAYME_CREATION_SUCCEEDED, {
        is_active: true,
      });
    } catch (error: any) {
      const errorMessage = getPayMeErrorMessage(error, t);
      setError(errorMessage);
      
      const errorDescription = errorMessage;
      amplitude.track(PAYME_EVENTS.PAYME_CREATION_FAILED, {
        error_description: errorDescription,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handlePreview = () => {
    setIsPreviewModalOpen(true);
    amplitude.track(PAYME_EVENTS.PAYME_PREVIEWED);
  };

  const handleSettings = () => {
    navigate(INNER_ROUTES.PAYME_SETTINGS);
    amplitude.track(PAYME_EVENTS.PAYME_SETTINGS_SELECTED);
  };

  const handleFirstTimeSetupShare = () => {
    setIsFirstTimeSetupModalOpen(false);
    setIsShareModalOpen(true);
    amplitude.track(PAYME_EVENTS.PAYME_SHARING_SUCCEEDED_TO_START);
  };

  const handleFirstTimeSetupViewSettings = () => {
    setIsFirstTimeSetupModalOpen(false);
    handleSettings();
  };

  const handleFirstTimeSetupDismiss = () => {
    setIsFirstTimeSetupModalOpen(false);
  };

  const handleShare = () => {
    setIsShareModalOpen(true);
    amplitude.track(PAYME_EVENTS.PAYME_SHARING_SUCCEEDED_TO_START);
  };

  const handleCloseShareModal = () => {
    setIsShareModalOpen(false);
  };

  const handleEditClick = () => {
    setIsEditConfirmModalOpen(true);
  };

  const handleEditConfirm = () => {
    setIsEditConfirmModalOpen(false);
    setEditHandleApiError(null); // Clear any previous errors
    setIsEditHandleModalOpen(true);
  };

  const handleEditCancel = () => {
    setIsEditConfirmModalOpen(false);
  };

  // Update handle name mutation
  const updateHandleNameMutation = useMutation({
    mutationFn: (newHandleName: string) => {
      return updatePaymentHandleName({
        currentHandleName: handle,
        newHandleName,
      });
    },
    onSuccess: (_, newHandleName) => {
      setHandle(newHandleName);
      setError(null);
      setEditHandleApiError(null);
      setIsEditHandleModalOpen(false);
      // Invalidate queries to refetch data
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_PAYMENT_HANDLE_CONFIG],
      });
    },
    onError: (error: any) => {
      const errorMessage = getPayMeErrorMessage(error, t);

      setError(errorMessage);
      setEditHandleApiError(errorMessage);
    },
  });

  const handleUpdateHandle = (newHandleName: string) => {
    updateHandleNameMutation.mutate(newHandleName);
  };

  const getPayMeLinkUrl = () => {
    if (variant === 'handle' && handle) {
      return `${PAYME_BASE_URL}/${handle}`;
    }
    return '';
  };

  const isCreateHandleDisabled =
    isLoading ||
    !handle.trim() ||
    handle.length > PAYME_HANDLE_MAX_LENGTH ||
    !isValidPayMeHandle(handle);

  // Render promo variant (simple card)
  if (variant === 'promo') {
    return (
      <div className={`${styles['payme-card']} ${styles['payme-card--promo']}`}>
        <div className={styles['payme-card__content']}>
          <div className={styles['payme-card__text-content']}>
            <Badge
              text={t('innerLayout.homePage.paymeCard.justLaunched')}
              color="green"
              size="sm"
            />
            <h2 className={styles['payme-card__title']}>
              {t('innerLayout.homePage.paymeCard.title')}
            </h2>
            <p className={styles['payme-card__description']}>
              {t('innerLayout.homePage.paymeCard.description')}
            </p>
            {canAddPaymentHandle && (
              <div className={styles['payme-card__button']}>
                <Button
                  color="primary"
                  variant="contained"
                  size="md"
                  label={t('innerLayout.homePage.paymeCard.buttonText')}
                  trailingIcon={ArrowRight}
                  onClickHandler={handleCreateLink}
                />
              </div>
            )}
          </div>
          <div className={styles['payme-card__illustration-section']}>
            <div className={styles['payme-card__illustration']}>
              <img
                src={paymeIllustration}
                alt="PayMe illustration"
                className={styles['payme-card__illustration-image']}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Render handle variant (form card)
  return (
    <div className={`${styles['payme-card']} ${styles['payme-card--handle']}`}>
      <div className={styles['payme-card__content']}>
        <div className={styles['payme-card__form-section']}>
          <h2 className={styles['payme-card__title']}>
            {t('innerLayout.paymePage.handleCreationCard.title')}
          </h2>
          {!isHandleCreated && !hasAddOrChangePermission ? (
            <p className={styles['payme-card__hint']}>
              {t('innerLayout.paymePage.handleCreationCard.noHandleOrAddPermission')}
            </p>
          ) : (
            <div className={styles['payme-card__input-row']}>
              <div className={styles['payme-card__input-wrapper']}>
                <div
                  ref={inputContainerRef}
                  className={styles['payme-card__input-container']}
                >
                  <PayMeHandleInput
                    id="payme-handle"
                    name="payme-handle"
                    value={handle}
                    onChange={handleInputChange}
                    placeholder={t(
                      'innerLayout.paymePage.handleCreationCard.handlePlaceholder'
                    )}
                    trailingIcon={
                      isHandleCreated ? undefined : error ? CircleInfo : Pen
                    }
                    disabled={isHandleCreated}
                    error={(!isHandleCreated && error) || undefined}
                    status={(!isHandleCreated && error) ? 'error' : 'default'}
                  />
                  {isHandleCreated && (
                    <div
                      className={styles['payme-card__actions-container']}
                    >
                      <div className={styles['payme-card__copy-button']}>
                        <CopyText textToCopy={getPayMeLinkUrl()} />
                      </div>
                      <div className={styles['payme-card__badge']}>
                        <Badge
                          text={
                            isActivated
                              ? t(
                                'innerLayout.paymePage.handleCreationCard.activeBadge'
                              )
                              : t(
                                'innerLayout.paymePage.handleCreationCard.inactiveBadge'
                              )
                          }
                          color={isActivated ? 'green' : 'red'}
                          size="sm"
                        />
                      </div>
                      {!isStaff && (
                        <div className={styles['payme-card__preview-button']}>
                          <Button
                            id="preview-button"
                            variant="link"
                            color="primary"
                            size="md"
                            leadingIcon={Eye}
                            label={
                              inputContainerWidth < RESPONSIVE_PREVIEW_BUTTON_WIDTH
                                ? undefined
                                : t(
                                  'innerLayout.paymePage.handleCreationCard.previewButton'
                                )
                            }
                            onClickHandler={handlePreview}
                          />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
              <div className={styles['payme-card__button-wrapper']}>
                {isHandleCreated ? (
                  <>
                    {canChangePaymentHandle && (
                      <Button
                        color="secondary"
                        variant="outlined"
                        size="md"
                        label={t(
                          'innerLayout.paymePage.handleCreationCard.settingsButton'
                        )}
                        leadingIcon={Gear}
                        onClickHandler={handleSettings}
                      />
                    )}
                    {canSharePaymentHandle && (
                      <Button
                        color="primary"
                        variant="contained"
                        size="md"
                        label={t(
                          'innerLayout.paymePage.handleCreationCard.shareButton'
                        )}
                        leadingIcon={PaperPlane}
                        onClickHandler={handleShare}
                        disabled={!isActivated}
                      />
                    )}
                  </>
                ) : (
                  canAddPaymentHandle && (
                    <Button
                      color="primary"
                      variant="contained"
                      size="md"
                      label={t(
                        'innerLayout.paymePage.handleCreationCard.buttonText'
                      )}
                      onClickHandler={handleCreateLink}
                      loading={isLoading}
                      disabled={isCreateHandleDisabled}
                    />
                  )
                )}
              </div>
            </div>
          )}
          {(isHandleCreated || hasAddOrChangePermission) &&
            (canChangeHandleName && isHandleCreated && canChangePaymentHandle ? (
              <p className={styles['payme-card__hint']}>
                {t(
                  'innerLayout.paymePage.handleCreationCard.editHint'
                )}{' '}
                {canChangePaymentHandle &&
                  (<Button
                    variant="link"
                    color="primary"
                    size="sm"
                    label={t(
                      'innerLayout.paymePage.handleCreationCard.editButton'
                    )}
                    onClickHandler={handleEditClick}
                  />
                  )}
              </p>
            ) : isHandleCreated ? (
              <p className={styles['payme-card__hint']}>
              </p>
            ) : (
              <p className={styles['payme-card__hint']}>
                {t('innerLayout.paymePage.handleCreationCard.hint')}
              </p>
            ))}
        </div>
        <div className={styles['payme-card__illustration-section']}>
          <div className={styles['payme-card__illustration']}>
            <img
              src={paymeIllustration}
              alt="PayMe illustration"
              className={styles['payme-card__illustration-image']}
            />
          </div>
        </div>
      </div>

      {isHandleCreated && (
        <>
          <PayMeShareModal
            open={isShareModalOpen}
            onClose={handleCloseShareModal}
            paymeLink={getPayMeLinkUrl()}
            handleName={handle}
            logoUrl={logoUrl}
          />
          <PayMeCheckoutPreviewModal
            open={isPreviewModalOpen}
            onClose={() => setIsPreviewModalOpen(false)}
            handle={handle}
            paymentHandleConfig={paymentHandleConfig || null}
            checkoutCustomizationConfig={checkoutCustomizationData || null}
          />
        </>
      )}

      <EditHandleConfirmModal
        open={isEditConfirmModalOpen}
        onClose={handleEditCancel}
        onConfirm={handleEditConfirm}
        isLoading={updateHandleNameMutation.isPending}
      />

      <EditHandleModal
        open={isEditHandleModalOpen}
        onClose={() => {
          setIsEditHandleModalOpen(false);
          setEditHandleApiError(null);
        }}
        onUpdate={handleUpdateHandle}
        currentHandle={handle}
        isLoading={updateHandleNameMutation.isPending}
        apiError={editHandleApiError}
      />

      <FirstTimeSetupModal
        open={isFirstTimeSetupModalOpen}
        onClose={handleFirstTimeSetupDismiss}
        onShare={handleFirstTimeSetupShare}
        onViewSettings={handleFirstTimeSetupViewSettings}
        paymeLink={getPayMeLinkUrl()}
        canShare={canSharePaymentHandle}
        canViewSettings={canChangePaymentHandle}
      />
    
    </div>
  );
};
